import { Component, OnInit } from '@angular/core';
import {
    faSmile,
    faMapSigns,
    faTimes,
    faMap,
    faFileAlt,
    faBinoculars,
    faAddressCard,
    faUser,
    faInfoCircle,
    faPenSquare,
    IconDefinition, faCheck, faPeopleArrows, faExchangeAlt, faMapPin, faTrash, faChevronRight, faSquareCheck,
    faFloppyDisk, faCodeFork
} from '@fortawesome/free-solid-svg-icons';
import {TenFourOrderService} from '../../../services/themes/10-4/ten-four-order.service';
import {Debtor} from '../../../services/debtor';
import {Creditor} from '../../../services/creditor';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, take} from 'rxjs/operators';
import {DebtorsService} from '../../../services/debtors.service';
import {ReferencesService} from '../../../services/references.service';
import {Reference} from '../../../services/reference';
import {OrdersService} from '../../../services/orders.service';
import {OrderStatesService} from '../../../services/order-states.service';
import {ProductsService} from '../../../services/products.service';
import {BranchesService} from '../../../services/branches.service';
import {CreditorsService} from '../../../services/creditors.service';
import {Order} from '../../../services/order';
import {Router} from '@angular/router';
import {CONTACT_TYPES_IDS} from '../../../../config/constants-10-4';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Contact} from '../../../services/contact';
import {TenFourContactEditComponent} from '../contact-edit/contact-edit.component';
import {PurchaseOrderSourcingModalComponent} from '../../../purchase-order-sourcing-modal/purchase-order-sourcing-modal.component';

interface IOptions {
    option: IconDefinition;
    text?: string;
    color?: string;
    onClick?: any;
    disabled?: boolean;
}

@Component({
    selector: 'app-step-1-order-details',
    templateUrl: './step-1-order-details.component.html',
    styleUrls: ['./step-1-order-details.component.scss'],
    providers: [OrdersService, OrderStatesService, ProductsService, DebtorsService, CreditorsService, ReferencesService, BranchesService],
})
export class TenFourStep1OrderDetailsComponent implements OnInit {

    order: Order | null | undefined;

    showChangeClientComponent = false;
    showChangeDeliveryContactComponent = false;

    loading = false;

    primaryOptions: IOptions[];
    secondaryOptions: IOptions[];

    constructor(public router: Router,
                public ordersService: TenFourOrderService,
                public debtorService: DebtorsService,
                public ngbModal: NgbModal,
                public creditorService: CreditorsService) {
    }

    ngOnInit(): void {
        this.ordersService.currentOrder.subscribe((order) => {
            this.order = order;
            this.primaryOptions = this.order ? [
                {
                    option: faSmile,
                    text: this.order.isCreditor() ? 'Ændre Kreditor' : 'Ændre Debitor',
                    onClick: () => {
                            this.showChangeDeliveryContactComponent = false;
                            this.showChangeClientComponent = !this.showChangeClientComponent;
                        },
                    color: 'yellow',
                },
                {
                    option: faMapSigns,
                    text: 'Opret ny leveringsadresse',
                    onClick: () => {
                        const modalRef = this.ngbModal.open(TenFourContactEditComponent, {size: 'lg'});

                        modalRef.componentInstance.contact = Contact.fromJSON({
                            contact_type_id: CONTACT_TYPES_IDS.DELIVERY_ADDRESS,
                            name: '',
                            nickname: '',
                        });

                        modalRef.componentInstance.saved = (contact: Contact) => {
                            this.loading = true;

                            const save$ = this.order.isCreditor()
                                ? this.creditorService.addCreditorsContact(this.order.creditor_id, contact)
                                : this.debtorService.addDebtorsContact(this.order.debtor_id, contact);

                            save$.subscribe(() => {
                                const newContact = Object.assign(new Contact(), contact);
                                newContact.contact_type_id = CONTACT_TYPES_IDS.DELIVERY_ADDRESS;

                                delete newContact._joinData;
                                delete newContact.contact_type;

                                const updated = this.ordersService.setNewContact(this.order, newContact);
                                this.updateClient(updated);
                            }, () => {
                                    this.loading = false;
                                });
                        };
                    },
                    color: 'limegreen',
                },
                {
                    option: faBinoculars,
                    text: 'Vis leveringsadresser',
                    onClick: () => {
                        this.showChangeClientComponent = false;
                        this.showChangeDeliveryContactComponent = !this.showChangeDeliveryContactComponent;
                    },
                    color: 'black',
                },
                {
                    option: faTrash,
                    text:  this.order.isFrozen() || this.order.isOrderLinesFrozen() ? 'Ordren er låst' : 'Slet Ordre',
                    onClick: () => {
                        if (confirm($localize `Are you sure?\n\nThe order will be deleted`)){
                            this.loading = true;
                            this.ordersService.deleteOrder(this.order).subscribe((response) => {
                                this.router.navigateByUrl('/orders');
                            });
                        }
                    },
                    color: '#F32013',
                    disabled: this.order.isFrozen() || this.order.isOrderLinesFrozen()
                },
                {
                    option: faCodeFork,
                    text: 'Købsordre',
                    onClick: () => {
                        let self = this;

                        const modalRef = this.ngbModal.open(PurchaseOrderSourcingModalComponent, {size: 'lg'});
                        modalRef.componentInstance.order = this.order;
                        modalRef.componentInstance.saved = () => {
                            this.ordersService.refreshOrder();
                        };
                    },
                    color: 'rgb(254, 210, 6)',
                }
            ] : [];

            this.secondaryOptions = this.order ? [
                {
                    option: faFloppyDisk,
                    text: this.order.isFrozen() || this.order.isOrderLinesFrozen() ? 'Ordren er låst' : 'Gem ordre',
                    onClick: () => {
                        this.saveCurrentOrder();
                    },
                    color: '#fff',
                    disabled: this.order.isFrozen() || this.order.isOrderLinesFrozen()
                }
            ] : [];
        });
    }

    saveCurrentOrder(): void {
        this.ordersService.currentOrder.pipe(take(1)).subscribe((order) => {
            if (order) {
                this.updateClient(order);
            }
        });
    }

    updateClient(updatedOrder: Order): void {
        this.loading = true;

        const previousDebtor = updatedOrder.debtor;
        const previousCreditor = updatedOrder.creditor;

        this.ordersService.updateOrder(updatedOrder).subscribe((response) => {
           this.loading = false;
           this.showChangeClientComponent = false;

           response.data.debtor = response.data.debtor || previousDebtor;
           response.data.creditor = response.data.creditor || previousCreditor;

           this.ordersService.setOrder(response.data);
        }, () => {
            this.loading = false;
        });
    }

    closeDeliveryContactComponent(event: boolean): void {
        this.showChangeDeliveryContactComponent = false;
    }

}
